/**
 * `ReportActions` — the controls every generated report carries, from the
 * demo's report pane (`renderReports`, demo lines 2657–2658):
 *
 * - **`ReportToolbar`** — Download, Copy text, Create Outlook draft (email
 *   only; a toast, as in the demo), Regenerate.
 * - **`ReportSidePanel`** — "Built from" with a source link per dataset, the
 *   file name, the definitions, and "Same report for" the other mines (or,
 *   for a weekly report, the other divisions).
 *
 * Download and Copy work on what the page rendered: the scene hands in a ref
 * to its document element, and `report-export.ts` wraps that markup in a
 * standalone HTML file. The email downloads as the `.eml` its transform built.
 * A `?download=1` query — the assistant's report card sets it — downloads
 * once the report is ready and then drops the query.
 *
 * Regenerate and "Same report for" ask the assistant, which is what the demo
 * did: the rebuild shows its generation steps in the chat and opens the
 * result here.
 *
 * UI layer (layer 4 of the 4-layer architecture).
 *
 * @see ./report-export.ts
 * @see docs/logic/reports/overview.md
 */

import { useCallback, useEffect, type ReactNode, type RefObject } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copy, Download, Mail, RefreshCw } from 'lucide-react';
import { askOem } from '../../shared/assistant/ask';
import { useBrand } from '../../shared/BrandContext';
import {
  REPORT_FORMAT,
  reportPrompt,
  siblingLabel,
  siblingRefs,
} from '../../shared/report-catalog';
import { SourceLink } from '../../shared/oem-tiles';
import { useToast } from '../../shared/toast/ToastContext';
import type { GeneratedReport, ReportRef, ReportSource } from '../../schema/report';
import { DOWNLOAD_MIME, reportHtmlFile } from './report-export';

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]';

const EYEBROW = 'text-[10px] font-semibold uppercase tracking-[0.08em]';

/** Demo 2658. The definitions every report shares, verbatim. */
export const REPORT_DEFINITIONS =
  'Availability = engine h ÷ (shifts × 9.5 scheduled operating h). All-in cost = labour + parts + ' +
  'freight + external + lost production. CO₂-e = diesel L × 2.68 + kWh × site grid factor. ' +
  'Condition bands: good ≥80 · fair 60–79 · poor 40–59 · very poor 20–39 · major issue <20.';

/** Demo 2665 — a plain browser download. */
function saveFile(name: string, text: string, mime: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 1500);
}

function ToolbarButton({
  onClick,
  primary,
  icon,
  children,
}: {
  onClick: () => void;
  primary?: boolean;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex min-h-[32px] items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] font-medium transition-opacity hover:opacity-90 ${FOCUS_RING}`}
      style={
        primary
          ? { background: 'var(--accent)', color: 'var(--accent-text)', borderColor: 'transparent' }
          : { background: 'var(--surface-2)', color: 'var(--ink-primary)', borderColor: 'var(--hairline)' }
      }
    >
      {icon}
      {children}
    </button>
  );
}

export interface ReportToolbarProps {
  report: GeneratedReport | null;
  address: ReportRef;
  /** The rendered document, for the HTML download and Copy text. */
  docRef: RefObject<HTMLElement>;
  /** The `.eml` text, on the email. */
  eml?: string;
  /** What Copy text copies, when it is not the document's own text. */
  copyText?: string;
}

/** Download · Copy text · Create Outlook draft · Regenerate. */
export function ReportToolbar({ report, address, docRef, eml, copyText }: ReportToolbarProps) {
  const { show: toast } = useToast();
  const { brand } = useBrand();
  const [params, setParams] = useSearchParams();

  const download = useCallback(() => {
    if (!report) return;
    if (report.download === 'eml' && eml) {
      saveFile(report.fileName, eml, DOWNLOAD_MIME.eml);
    } else if (report.download === 'html' && docRef.current) {
      const file = reportHtmlFile(
        report.title,
        docRef.current.outerHTML,
        brand.accentHex,
        window.location.origin,
      );
      saveFile(report.fileName, file, DOWNLOAD_MIME.html);
    } else {
      toast('No file export for this report');
      return;
    }
    toast(`Downloading ${report.fileName}`);
  }, [brand.accentHex, docRef, eml, report, toast]);

  // The assistant's card links here with `?download=1` — from another page or
  // this one. Honour it once the document has rendered, then drop the query
  // so a reload or a re-render does not download twice.
  const wantsDownload = params.get('download') === '1';
  useEffect(() => {
    if (!wantsDownload || !report || !docRef.current) return;
    download();
    const next = new URLSearchParams(params);
    next.delete('download');
    setParams(next, { replace: true });
  }, [docRef, download, params, report, setParams, wantsDownload]);

  const copy = () => {
    const text = copyText ?? docRef.current?.innerText ?? '';
    if (!navigator.clipboard || !text) {
      toast('Copy is not available in this browser');
      return;
    }
    navigator.clipboard
      .writeText(text)
      .then(() => toast(report?.kind === 'email' ? 'Email text copied' : 'Report text copied'))
      .catch(() => toast('Copy was blocked by the browser'));
  };

  const fmt = (report?.download ?? REPORT_FORMAT[address.kind]).toUpperCase();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ToolbarButton primary onClick={download} icon={<Download className="h-3.5 w-3.5" aria-hidden="true" />}>
        Download {fmt}
      </ToolbarButton>
      <ToolbarButton onClick={copy} icon={<Copy className="h-3.5 w-3.5" aria-hidden="true" />}>
        Copy text
      </ToolbarButton>
      {address.kind === 'email' ? (
        <ToolbarButton
          onClick={() =>
            toast('In production this creates the draft in Outlook through the connector, addressed as shown')
          }
          icon={<Mail className="h-3.5 w-3.5" aria-hidden="true" />}
        >
          Create Outlook draft
        </ToolbarButton>
      ) : null}
      <ToolbarButton
        onClick={() => askOem(reportPrompt(address))}
        icon={<RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
      >
        Regenerate
      </ToolbarButton>
    </div>
  );
}

export interface ReportSidePanelProps {
  /** Set by `<Commentable>` (cloneElement); forwarded to the root element so the anchor reaches the DOM. */
  'data-comment-anchor'?: string;
  report: GeneratedReport;
  address: ReportRef;
  /** Opens the rows behind one "Built from" line; `null` for a line with none. */
  onSource: (source: ReportSource) => (() => void) | null;
}

/** Built from · File · Definitions · Same report for. */
export function ReportSidePanel({
  report,
  address,
  onSource,
  'data-comment-anchor': commentAnchor,
}: ReportSidePanelProps) {
  const siblings = siblingRefs(address);
  return (
    <aside
      data-comment-anchor={commentAnchor}
      className="min-w-0 space-y-3 rounded-2xl border p-4 text-[12px]"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
      aria-label="About this report"
    >
      <div>
        <div className={EYEBROW} style={{ color: 'var(--ink-tertiary)' }}>
          Built from
        </div>
        <ul className="mt-1.5 space-y-1">
          {report.sources.map((s) => {
            const open = onSource(s);
            return (
              <li key={s.label} className="flex items-baseline justify-between gap-3">
                <span style={{ color: 'var(--ink-secondary)' }}>{s.label}</span>
                {open ? (
                  <SourceLink onClick={open}>{s.count}</SourceLink>
                ) : (
                  <span className="mono text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
                    {s.count}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div>
        <div className={EYEBROW} style={{ color: 'var(--ink-tertiary)' }}>
          File
        </div>
        <div className="mono mt-1 break-all text-[11px]" style={{ color: 'var(--ink-secondary)' }}>
          {report.fileName}
        </div>
        {report.download === 'html' ? (
          <p className="mt-1 text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
            Saved as a self-contained HTML rendering of this document. In production it is exported
            as {REPORT_FORMAT[report.kind].toUpperCase()} through the document connector.
          </p>
        ) : null}
      </div>

      <div>
        <div className={EYEBROW} style={{ color: 'var(--ink-tertiary)' }}>
          Definitions
        </div>
        <p className="mt-1 text-[11.5px] leading-[1.5]" style={{ color: 'var(--ink-secondary)' }}>
          {REPORT_DEFINITIONS}
        </p>
      </div>

      {siblings.length ? (
        <div>
          <div className={EYEBROW} style={{ color: 'var(--ink-tertiary)' }}>
            Same report for
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {siblings.map((ref) => (
              <button
                key={siblingLabel(ref)}
                type="button"
                onClick={() => askOem(reportPrompt(ref))}
                className={`rounded-full border px-2.5 py-1 text-[11.5px] transition-colors hover:bg-[var(--surface-1)] ${FOCUS_RING}`}
                style={{ borderColor: 'var(--hairline)', color: 'var(--ink-primary)' }}
              >
                {siblingLabel(ref)}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </aside>
  );
}
